import { useMemo } from "react";
import { UserRole } from "@comp-sys/shared";
import { useAuthStore } from "@/stores/auth-store";
import { NAV_ITEMS } from "@/lib/constants/navigation";

/**
 * Check if a user role is allowed to access a navigation item
 */
function isRoleAllowed(
  userRole: UserRole | undefined,
  allowedRoles: UserRole[] | undefined
): boolean {
  // If no roles specified, item is visible to all
  if (!allowedRoles || allowedRoles.length === 0) {
    return true;
  }

  // If no user role, deny access
  if (!userRole) {
    return false;
  }

  return allowedRoles.includes(userRole);
}

/**
 * PURE helper: can a given activeRole access the route identified by `href`?
 * Mirrors `useCanAccessRoute` but takes activeRole as an arg (no hook / store read),
 * so it can be called inside event handlers (e.g. after a role switch).
 *
 * - If `href` is not found in NAV_ITEMS, returns true (page-level guards handle the rest).
 * - Otherwise returns `isRoleAllowed(activeRole, item.roles)`.
 *
 * Reused by RoleSwitcher's route re-check and by Task 11's role-select page.
 */
export function canAccessRoute(activeRole: UserRole, href: string): boolean {
  const item = NAV_ITEMS.find((nav) => nav.href === href);
  if (!item) {
    // Route not in navigation — allow (page-level guards handle it).
    return true;
  }
  return isRoleAllowed(activeRole, item.roles);
}

/**
 * PURE helper: the canonical landing route for a given role after a context switch.
 * Reused by RoleSwitcher's post-switch redirect and Task 11's role-select page.
 */
export function roleHome(role: UserRole): string {
  switch (role) {
    case UserRole.JUDGE:
      return "/dashboard/judge";
    case UserRole.COMPETITION_MANAGER:
    case UserRole.ADMIN:
    case UserRole.SUPER_ADMIN:
    case UserRole.SYSTEM_ADMIN:
      return "/dashboard";
    case UserRole.SCHOOL:
      return "/dashboard/school";
    case UserRole.INSTITUTION:
      return "/dashboard/institution";
    case UserRole.CONTESTANT:
    case UserRole.COACH:
    case UserRole.VOLUNTEER:
    default:
      return "/dashboard";
  }
}

/**
 * Hook to get navigation items filtered by current user's role.
 */
export function useNavigation() {
  const user = useAuthStore((state) => state.user);

  const filteredNavItems = useMemo(() => {
    const items = NAV_ITEMS.filter((item) =>
      isRoleAllowed(user?.activeRole, item.roles)
    );

    return items;
  }, [user?.activeRole]);

  return {
    navItems: filteredNavItems,
    userRole: user?.activeRole,
  };
}

/**
 * Check if current user can access a specific route
 */
export function useCanAccessRoute(href: string): boolean {
  const user = useAuthStore((state) => state.user);
  return canAccessRoute(user?.activeRole as UserRole, href);
}
